import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { Fragment, HTMLAttributes, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Colors } from 'styles';
import { Ellipsis } from 'common/ui/Ellipsis';
import TableLoading from 'common/TableLoading';
import useInfiniteScroll, { UseInfiniteScrollHookResult } from 'react-infinite-scroll-hook';
import type {
  Row as ReactRow,
  Table as ReactTable,
  ColumnDef,
  HeaderGroup,
  CellContext,
  RowSelectionState,
  OnChangeFn,
  TableState,
  InitialTableState,
} from '@tanstack/react-table';
import styled, { css } from 'styled-components';
import TextWithTooltip from './TextWithTooltip';
import { NOOP } from 'config/constants';
import { TableOverlay } from './TableOverlay';

export declare type DataUpdater<TData> = (
  cell: CellContext<TData, unknown>,
  value: unknown
) => unknown;

declare module '@tanstack/react-table' {
  interface TableMeta<TData> {
    dataUpdater: DataUpdater<TData>;
  }
}

export type TableRowProps<TData> = {
  row: ReactRow<TData>;
  role: string;
  ['aria-label']: string;
  highlightRow: boolean;
  tableHelpers: ReactTable<TData>;
  minTableWidth: number;
  rowCss?: string;
  children?: React.ReactNode;
  className?: string;
  isSelected?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export type RenderRowProps<TData> = {
  row: ReactRow<TData>;
  bodyRowProps?: Partial<TableRowProps<TData>>;
  cellProps?: any;
};

export const DefaultRow = <TData extends any>({
  row,
  highlightRow,
  minTableWidth,
  role,
  ...props
}: TableRowProps<TData>): React.ReactElement => (
  <div
    css={`
      display: flex;
      align-items: center;
      ${highlightRow
        ? `
      &:hover,
      &:focus {
        opacity: 1;
        background-color: ${Colors.gray3};
      }
    `
        : ''}
      min-width: ${minTableWidth}px;
      display: flex;
      flex: 1 0 auto;
      ${props.rowCss ?? ''}
    `}
    role="row"
    {...props}
  />
);

export type TableBodyProps<TData> = {
  hasData: boolean;
  hasNextPage: boolean;
  loading: boolean;
  renderRow: (arg1: RenderRowProps<TData>) => React.ReactElement;
  rootRef: (node: Element | null) => void;
  rows: Array<ReactRow<TData>>;
  sentryRef: UseInfiniteScrollHookResult[0];
  cellProps?: any;
  onScroll?: () => void;
  dataTestId?: string;
  className?: string;
};

export const DefaultBody = <TData extends any>({
  hasData,
  hasNextPage,
  loading,
  renderRow,
  rootRef,
  rows,
  cellProps = {},
  sentryRef,
  onScroll,
  dataTestId,
}: TableBodyProps<TData>): React.ReactElement => (
  <div
    className="table-body"
    ref={rootRef}
    onScroll={onScroll}
    css="flex: 1; overflow: auto; position: relative;"
    data-testid={dataTestId}
  >
    {/* To confine the TableLoading overlay to the table body and prevent
          flashing over static parents, place it here as a child and set
          `relative` position on the table body. */}
    <TableLoading loading={loading} hasData={hasData} />
    {rows.map((row) => renderRow({ row, cellProps }))}
    {hasNextPage && (
      <div key="loading" data-testid="loading">
        Loading...
      </div>
    )}
    {/* Avoid the sentry prematurely entering the root while the table is still loading.
     * Intersection Observer only fires on changes in intersection threshold, so if it is
     * still within the root + margin, loadMore won't fire.
     * This system is still not perfect; see RP-2176
     */}
    {hasNextPage && !loading && <div ref={sentryRef} />}
  </div>
);

const DEFAULT_COLUMN_PROPS_FOR_ALL_TABLES = {
  minSize: 0,
} as const;

const NO_TOOLTIPS: Array<number> = [];

type TableStateOptional = Partial<TableState>;
type ColumnLayoutMeta = {
  disableFlexGrow?: boolean;
};

type TableProps<TData> = {
  columns: Array<ColumnDef<TData>>;
  data: Array<TData>;
  className?: string;
  Row?: (arg1: TableRowProps<TData>) => React.ReactElement;
  Body?: (arg1: TableBodyProps<TData>) => React.ReactElement;
  highlightRow?: boolean;
  showHeaderSeparators?: boolean;
  onLoadMore?: () => unknown | null;
  hasNextPage?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  colsWithTooltip?: Array<number>;
  loading?: boolean;
  dataUpdater?: DataUpdater<TData>;
  tableRef?: {
    current: ReactTable<TData> | null;
  };
  css?: string;
  rowCss?: string;
  ['data-testid']?: string;
  defaultColumn?: ColumnDef<TData>;
  cellProps?: any;
  headerCss?: string;
  rowSelection?: {
    [key: string]: boolean;
  };
  setRowSelection?: (arg1: { [key: string]: boolean }) => void;
  enableMultiRowSelection?: boolean;
  manualSorting?: boolean;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  onRowClick?: (evt: any, row: TData) => void;
  getRowId?: (row: TData) => string;
  state?: TableStateOptional;
  initialState?: InitialTableState;
  autoResetPageIndex?: boolean;
};

/**
 * WARNING- make sure EVERYTHING going into the table is properly memoized.
 * That includes default properties/empty objects/empty arrays.
 * Failure to do so may re-memoize Body/Row definitions and cause internal
 * cells to lose their identity (breaking focused input cells, for example.)
 */
export const Table = <TData extends any>({
  columns,
  data,
  className,
  Row = DefaultRow,
  Body = DefaultBody,
  highlightRow = false,
  showHeaderSeparators = false,
  onLoadMore = NOOP,
  hasNextPage = false,
  disabled = false,
  disabledMessage = '',
  loading = false,
  colsWithTooltip = NO_TOOLTIPS,

  // Custom function that we supplied to our table instance,
  // so we can call it from our cell renderer (helps prevent editable table fields from losing inputFocus from rerendering)
  // https://codesandbox.io/s/github/tannerlinsley/react-table/tree/v7/examples/editable-data
  dataUpdater = NOOP,

  'data-testid': dataTestId,
  tableRef = { current: null },
  css,
  cellProps,
  rowCss,
  headerCss = '',
  setRowSelection = NOOP,
  rowSelection = {},
  enableMultiRowSelection = false,
  manualSorting = false,
  ...reactTableProps
}: TableProps<TData>): React.ReactElement => {
  const [sentryRef, { rootRef }] = useInfiniteScroll({
    onLoadMore,
    loading,
    hasNextPage,
    rootMargin: '0px 0px 600px 0px',
  });

  // Use the state and functions returned from useReactTable to build your UI
  const tableHelpers = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: DEFAULT_COLUMN_PROPS_FOR_ALL_TABLES,
    enableMultiRowSelection,
    // @ts-expect-error [EN-10263] - TS2322 - Type '(arg1: { [key: string]: boolean; }) => void' is not assignable to type 'OnChangeFn<RowSelectionState>'.
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    meta: {
      dataUpdater,
    },
    getFilteredRowModel: getFilteredRowModel(),
    manualSorting,
    ...reactTableProps,
  });

  const { getHeaderGroups, getRowModel } = tableHelpers;

  const headerGroups = getHeaderGroups();

  const minTableWidth = getMinTableWidth(headerGroups);

  const rows = getRowModel().rows;

  const scrollableRootRef = useRef<Element | null>(null);
  const lastScrollDistanceToTopRef = useRef<number | undefined>();

  const rootRefSetter = useCallback(
    (node: Element | null) => {
      rootRef(node);
      scrollableRootRef.current = node;
    },
    [rootRef]
  );

  // capture the current scrollTop position within the table list
  const handleRootScroll = useCallback(() => {
    const rootNode = scrollableRootRef.current;
    if (rootNode) {
      lastScrollDistanceToTopRef.current = rootNode.scrollTop;
    }
  }, []);

  // keep the scroll position when new items are added
  useLayoutEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    if (scrollableRoot) {
      scrollableRoot.scrollTop = lastScrollDistanceToTopRef.current ?? 0;
    }
  }, [rows, rootRef]);

  useEffect(() => {
    tableRef.current = tableHelpers;
  }, [tableHelpers, tableRef]);

  const renderRow = useCallback(
    ({ row, bodyRowProps, cellProps = {} }: RenderRowProps<TData>) => {
      const cells = row.getAllCells();

      return (
        <Row
          isSelected={row.getIsSelected()}
          key={row.id}
          row={row}
          role="row"
          aria-label={'row-' + row.index}
          highlightRow={highlightRow && !row.getIsSelected()}
          tableHelpers={tableHelpers}
          minTableWidth={minTableWidth}
          rowCss={rowCss}
          {...bodyRowProps}
          className={`table-row${row.getIsSelected() ? '-selected' : ''}`}
        >
          {cells.map((cell, index) => {
            const withTooltip = colsWithTooltip.includes(index);
            const size = cell.column.getSize();
            const cellColumnMeta = cell.column.columnDef.meta as ColumnLayoutMeta | undefined;
            const flexWidth = cellColumnMeta?.disableFlexGrow ? '0 0 auto' : `${size} 0 auto`;
            return withTooltip ? (
              <TextWithTooltip
                customEllipsisCss={{
                  flex: flexWidth,
                  minWidth: `${cell.column.columnDef.minSize ?? 0}px`,
                  width: `${size}px`,
                  boxSizing: `border-box`,
                }}
                // @ts-expect-error [incompatible-type]
                name={flexRender(cell.column.columnDef.cell, {
                  ...cell.getContext(),
                  ...cellProps,
                })}
              />
            ) : (
              <Ellipsis
                key={cell.id}
                css={`
                  flex: ${flexWidth};
                  ${cell.column.columnDef.minSize != null
                    ? `min-width: ${cell.column.columnDef.minSize ?? 0}px;`
                    : ''}
                  width: ${size}px;
                  box-sizing: border-box;
                `}
                role="cell"
              >
                {flexRender(cell.column.columnDef.cell, { ...cell.getContext(), ...cellProps })}
              </Ellipsis>
            );
          })}
        </Row>
      );
    },
    [Row, highlightRow, tableHelpers, minTableWidth, rowCss, colsWithTooltip]
  );

  // Render the UI for your table
  return (
    <div
      role="table"
      css={`
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        position: relative;
        ${css ?? ''}
      `}
      className={className}
      data-testid={dataTestId}
    >
      {disabled && <TableOverlay message={disabledMessage} />}
      <div role="rowgroup" className="table-header">
        {headerGroups.map((headerGroup) => (
          <div
            key={headerGroup.id}
            role="row"
            css={`
              min-width: ${minTableWidth}px;
              display: flex;
              flex: 1 0 auto;
            `}
          >
            {headerGroup.headers.map((header, index) => {
              const size = header.getSize();
              const headerColumnMeta = header.column.columnDef.meta as ColumnLayoutMeta | undefined;
              const flexWidth = headerColumnMeta?.disableFlexGrow ? '0 0 auto' : `${size} 0 auto`;
              return (
                <Fragment key={header.id}>
                  <div
                    css={`
                      font-weight: 500;
                      padding: 5px;
                      color: ${Colors.gray8};
                      box-sizing: border-box;
                      flex: ${flexWidth};
                      min-width: ${header.column.columnDef.minSize}px;
                      width: ${size}px;
                      ${headerCss}
                    `}
                    role="columnheader"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                  {showHeaderSeparators && index < headerGroup.headers.length - 1 && (
                    <div
                      css={`
                        position: relative;
                        width: 1px;
                        height: 18px;
                        top: 6px;
                        right: 8px;
                        background-color: ${Colors.gray10};
                      `}
                    ></div>
                  )}
                </Fragment>
              );
            })}
          </div>
        ))}
      </div>
      <Body
        className="table-body"
        hasData={data.length > 0}
        hasNextPage={hasNextPage}
        loading={loading}
        renderRow={renderRow}
        rootRef={rootRefSetter}
        onScroll={handleRootScroll}
        rows={rows}
        cellProps={cellProps}
        sentryRef={sentryRef}
        dataTestId={`${dataTestId ?? 'table'}-body`}
      />
    </div>
  );
};

type SelectableRowProps = TableRowProps<unknown>;

export const StyledRow = styled(DefaultRow).attrs((props: SelectableRowProps) => ({
  ...props,
  onClick: props.row.getToggleSelectedHandler(),
  'aria-selected': props.row.getIsSelected() ? 'true' : 'false',
  'min-width': `${props.minTableWidth}px`,
  display: 'flex',
  flex: '1 0 auto',
}));

export const SelectableRow = StyledRow`
  background-color: ${(props: SelectableRowProps) =>
    props.row.getIsSelected() ? Colors.gray5 : 'transparent'};
  cursor: pointer;
  ${(props: SelectableRowProps) =>
    props.highlightRow &&
    !props.row.getIsSelected() &&
    css`
      &:hover,
      &:focus {
        opacity: 1;
        background-color: ${Colors.gray3};
      }
    `}
`;

export function getMinTableWidth<TData>(headerGroups: Array<HeaderGroup<TData>>): number {
  const deepestHeaderLevel = headerGroups.length - 1;
  return headerGroups[deepestHeaderLevel].headers.reduce(
    (groupWidth, header) => groupWidth + (header.column.columnDef.minSize ?? 0),
    0
  );
}
